import React from 'react';
import axios from 'axios';
import { useFormikContext } from 'formik';
import { isEqual } from 'lodash';
import * as Yup from 'yup';

import {
  isSysconfigPermissionAction,
  PERMISSION_ACTIONS_CONFIG,
  PERMISSION_VERBS_CONFIG,
} from '~/components/core/Permissions/PermissionUtils';
import { getVisibleSections, isPermissionItemVisible } from '~/components/core/Permissions/Utils.ts';
import { useCms } from '~/components/hooks/useCms';
import {
  isSysconfigScopeAvailableForOrganization,
  pickAvailabilityOperationalDetails,
} from '~/components/SystemConfiguration/sysconfigScopeAvailability';
import { useSysconfig } from '~/components/SystemConfiguration/SystemConfigurationScreen';
import PERMISSIONS_DESCRIPTIONS from '~/server_shared/generated-types/PERMISSIONS_DESCRIPTIONS';

import ConfirmDialogContent from '../../../Users/UserDialogs/UserPermissionsSection/ConfirmDialogContent';
import { PROFILES_ROUTES } from '../routes';

export const getPermissionsFieldId = (id) => `permissions.${id}`;

export const FORM_FIELDS = {
  id: 'id',
  display_name: 'display_name',
  description: 'description',
  default_permission: 'default_permission',
  permissions: 'permissions',
  users: 'users',
};

/**
 * @deprecated Use `useFormInitialValues` hook for production code.
 * This function is kept for test utilities where React context is not available.
 * It initializes ALL permissions (including hidden ones), which can cause issues
 * when submitted to the backend.
 */
export const formInitialValues = (isTest = false) => ({
  [FORM_FIELDS.display_name]: '',
  [FORM_FIELDS.description]: '',
  [FORM_FIELDS.default_permission]: '',
  [FORM_FIELDS.permissions]: Object.fromEntries(
    Object.keys(PERMISSION_ACTIONS_CONFIG).map((permissionId) => [
      permissionId,
      isTest ? PERMISSION_VERBS_CONFIG.FORBIDDEN.optionValue : '',
    ])
  ),
  [FORM_FIELDS.users]: [],
});

/**
 * Get permission actions that are visible based on organization features, payments
 * configuration, and — for the granular sysconfig scopes — whether the organization
 * can use the area the scope gates (the same predicates that gate the sysconfig
 * tiles, see sysconfigScopeAvailability). Filters out hidden permissions (e.g.,
 * CONTACT_FINANCE_APPROVER when require_verification_for_contact_finance_edit is
 * disabled, or SYSCONFIG_DELEGATED_AUTHORITY when the org has no delegated authority).
 */
export const getVisiblePermissionActions = (organization, organizationOperationalDetails) => {
  const paymentsConfiguration = organizationOperationalDetails?.paymentsConfiguration;
  const visibleSections = organization ? getVisibleSections(SECTIONS, organization) : SECTIONS;

  return Object.values(PERMISSION_ACTIONS_CONFIG).filter((action) => {
    if (action.isSectionHeader) {
      return visibleSections.includes(action);
    } else {
      const sectionVisible = visibleSections.some((section) => section.id === action.section?.id);
      return (
        sectionVisible &&
        isPermissionItemVisible(action, paymentsConfiguration, organization) &&
        isSysconfigScopeAvailableForOrganization(action.id, { organization, organizationOperationalDetails })
      );
    }
  });
};

/**
 * Hook to get form initial values with only visible permissions.
 * Hidden permissions are excluded to prevent empty values from being submitted to the backend.
 */
export const useFormInitialValues = () => {
  const { organization: sysconfigOrganization, organizationOperationalDetails } = useSysconfig();
  const { userOrganization } = useCms();
  const organization = sysconfigOrganization ?? userOrganization;

  const visiblePermissionIds = getVisiblePermissionActions(organization, organizationOperationalDetails).map(
    (action) => action.id
  );

  return {
    [FORM_FIELDS.display_name]: '',
    [FORM_FIELDS.description]: '',
    [FORM_FIELDS.default_permission]: '',
    [FORM_FIELDS.permissions]: Object.fromEntries(visiblePermissionIds.map((permissionId) => [permissionId, ''])),
    [FORM_FIELDS.users]: [],
  };
};

export const profileToInitialValues = (profile, isVisible = () => true) => {
  // The backend's verb array is normalized to the canonical option value, so it matches a radio option.
  const permissions = Object.fromEntries(
    profile.profile_permissions
      .filter(({ permission_action }) => {
        const action = PERMISSION_ACTIONS_CONFIG[permission_action];
        return action && isVisible(action);
      })
      .map(({ permission_action, permission_verbs }) => [
        permission_action,
        PERMISSION_VERBS_CONFIG.getVerbByOptionValue(permission_verbs.sort())?.optionValue, // verbs are sorted as a hack to account for db entries that have "WRITE,READ" instead of "READ,WRITE"
      ])
  );

  return {
    [FORM_FIELDS.id]: profile.id,
    [FORM_FIELDS.display_name]: profile.display_name,
    [FORM_FIELDS.description]: profile.description ?? '',
    [FORM_FIELDS.default_permission]: PERMISSION_VERBS_CONFIG.getVerbByOptionValue(profile.defaults.sort())
      ?.optionValue,
    [FORM_FIELDS.permissions]: permissions,
    [FORM_FIELDS.users]: profile.user_ids,
  };
};

export const getInitialTouched = () => ({
  [FORM_FIELDS.display_name]: false,
  [FORM_FIELDS.description]: false,
  [FORM_FIELDS.default_permission]: false,
  [FORM_FIELDS.permissions]: {},
});

// Exported for testing
export const buildPermissionProfileValidationSchema = ({
  currentProfileName,
  organization,
  organizationOperationalDetails,
}) => {
  const paymentsConfiguration = organizationOperationalDetails?.paymentsConfiguration;
  const visiblePermissionActions = getVisiblePermissionActions(organization, organizationOperationalDetails);

  return Yup.object().shape({
    [FORM_FIELDS.display_name]: Yup.string()
      .max(128, 'Profile Name must be 128 characters or fewer')
      .test({
        name: 'unique_profile_name',
        message: 'Profile name must be unique',
        test: async (value) => {
          const { data } = await axios.get(PROFILES_ROUTES.ACTIVE(organization.id));
          const currentProfileNames = data.map(({ display_name }) => display_name);
          return !currentProfileNames.includes(value) || (currentProfileName && value === currentProfileName);
        },
      })
      .required('Required'),
    [FORM_FIELDS.description]: Yup.string()
      .required('Required')
      .max(128, 'Description must be 128 characters or fewer'),
    [FORM_FIELDS.default_permission]: Yup.array().required('Required'),
    [FORM_FIELDS.permissions]: Yup.object().shape(
      Object.fromEntries(
        visiblePermissionActions.map((permissionAction) => {
          let baseValidation = Yup.array().min(1, 'Required').required('Required');
          if (permissionAction.preconditions) {
            // Filter out preconditions for non-visible permissions (e.g., CONTACT_FINANCE_APPROVER
            // when require_verification_for_contact_finance_edit is disabled)
            const visiblePreconditions = permissionAction.preconditions.filter(({ field }) =>
              isPermissionItemVisible(field, paymentsConfiguration, organization)
            );
            visiblePreconditions.forEach(({ field, needsToBeAtLeast, whenVerbIsEqualOrGreaterThan, errorMessage }) => {
              baseValidation = baseValidation.test({
                name: `precondition_${permissionAction.id}__${field.id}`,
                message: errorMessage,
                test: (value, testContext) => {
                  if (!value || (Array.isArray(value) && value.length === 0)) {
                    // case is handled by baseValidation's "Required" validators
                    return true;
                  }
                  const currentValueVerbConfig = PERMISSION_VERBS_CONFIG.getVerbByOptionValue(value);
                  if (currentValueVerbConfig.isEqualOrGreaterThan(whenVerbIsEqualOrGreaterThan)) {
                    const preconditionFieldValue = testContext.parent[field.id];
                    if (
                      !preconditionFieldValue ||
                      (Array.isArray(preconditionFieldValue) && preconditionFieldValue.length === 0)
                    ) {
                      // The precondition field will have a "Required" error in this case
                      return true;
                    }
                    const currentPreconditionFieldVerbConfig =
                      PERMISSION_VERBS_CONFIG.getVerbByOptionValue(preconditionFieldValue);
                    return currentPreconditionFieldVerbConfig.isEqualOrGreaterThan(needsToBeAtLeast);
                  }

                  return true;
                },
              });
            });
          }
          return [permissionAction.id, baseValidation];
        })
      )
    ),
  });
};

export const usePermissionProfileValidationSchema = (currentProfileName) => {
  const { organization: sysconfigOrganization, organizationOperationalDetails } = useSysconfig();
  const { userOrganization } = useCms();
  const organization = sysconfigOrganization ?? userOrganization;
  const operationalDetailsRef = organizationOperationalDetails;
  // Stabilize the operational-details reference on the fields the schema actually
  // consumes (payments configuration + the scope-availability picker) to prevent
  // unnecessary schema rebuilds when the context returns fresh object identities.
  const operationalDetailsKey = JSON.stringify({
    paymentsConfiguration: operationalDetailsRef?.paymentsConfiguration,
    availability: pickAvailabilityOperationalDetails(operationalDetailsRef),
  });
  const stableOperationalDetails = React.useMemo(
    () => operationalDetailsRef,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [operationalDetailsKey]
  );

  return React.useMemo(
    () =>
      buildPermissionProfileValidationSchema({
        currentProfileName,
        organization,
        organizationOperationalDetails: stableOperationalDetails,
      }),
    [currentProfileName, organization, stableOperationalDetails]
  );
};

export const SECTIONS = Object.values(PERMISSION_ACTIONS_CONFIG)
  .filter((permission) => permission.isSectionHeader)
  .sort((a, b) => a.sectionHeaderIndex - b.sectionHeaderIndex);

// Create a hook to get filtered sections based on feature flags
export const useFilteredSections = () => {
  const { organization: sysconfigOrganization } = useSysconfig();
  const { userOrganization } = useCms();

  return React.useMemo(() => {
    // Filter sections based on their feature flag requirements
    return getVisibleSections(SECTIONS, sysconfigOrganization ?? userOrganization);
  }, [sysconfigOrganization, userOrganization]);
};

// Hook to check permission item visibility based on payments configuration, feature
// flags, and — for granular sysconfig scopes — the organization's area availability.
export const usePermissionItemVisibility = () => {
  const { organization: sysconfigOrganization, organizationOperationalDetails } = useSysconfig();
  const { userOrganization } = useCms();
  const organization = sysconfigOrganization ?? userOrganization;
  const operationalDetailsRef = organizationOperationalDetails;
  // Stabilize the operational-details reference on the fields the predicate actually
  // consumes to prevent unnecessary callback recreations.
  const operationalDetailsKey = JSON.stringify({
    paymentsConfiguration: operationalDetailsRef?.paymentsConfiguration,
    availability: pickAvailabilityOperationalDetails(operationalDetailsRef),
  });
  const stableOperationalDetails = React.useMemo(
    () => operationalDetailsRef,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [operationalDetailsKey]
  );

  return React.useCallback(
    (item) =>
      isPermissionItemVisible(item, stableOperationalDetails?.paymentsConfiguration, organization) &&
      isSysconfigScopeAvailableForOrganization(item.id, {
        organization,
        organizationOperationalDetails: stableOperationalDetails,
      }),
    [stableOperationalDetails, organization]
  );
};

export const SECTION_TO_ITEMS = Object.fromEntries(
  Object.keys(PERMISSION_ACTIONS_CONFIG).map((sectionId) => [
    sectionId,
    Object.values(PERMISSION_ACTIONS_CONFIG).filter((item) => item.section?.id === sectionId),
  ])
);

export const getSectionItems = (section) => SECTION_TO_ITEMS[section.id];

/**
 * Whether the section has any inner item to show. `isVisible` is the
 * feature-flag / payments-config predicate from `usePermissionItemVisibility`: a
 * section whose every item is hidden for this organization has nothing to expand
 * into, so the header must not offer a drawer or write the hidden items' values.
 */
export const doesSectionHaveInnerItems = (section, isVisible = () => true) =>
  SECTION_TO_ITEMS[section.id].some(isVisible);

export const useSetAllTouched = () => {
  const { setTouched, values } = useFormikContext();
  const valuesRef = React.useRef(values);
  valuesRef.current = values;

  return React.useCallback(() => {
    const currValues = valuesRef.current;
    setTouched({
      ...Object.fromEntries(Object.keys(currValues).map((key) => [key, true])),
      permissions: Object.fromEntries(Object.keys(currValues.permissions).map((key) => [key, true])),
    });
  }, [setTouched]);
};

export const saveProfileWithConfirmProps = {
  title: '',
  primaryButtonName: 'Yes',
  secondaryButtonName: 'Cancel',
  maxWidth: 'lg',
  contentText: (
    <ConfirmDialogContent
      title="Edit Permission Profile?"
      subtitle="This action will update the permission profile for all users assigned to it"
    />
  ),
};

const getMaxAllowedDefaultVerb = (profileDefaultVerb, allowedVerbsIds) => {
  if (!profileDefaultVerb || !allowedVerbsIds) {
    return profileDefaultVerb;
  }
  const maxAllowedDefaultVerb = allowedVerbsIds
    .map((verbId) => PERMISSION_VERBS_CONFIG.getVerbById(verbId))
    .reduce((max, verb) => {
      if (verb.isEqualOrLessThan(profileDefaultVerb) && (!max || verb.isEqualOrGreaterThan(max))) {
        return verb;
      }
      return max;
    }, null);
  return maxAllowedDefaultVerb;
};

/**
 * Drop permission entries whose actions aren't currently visible per
 * feature-flag / payments-config gates. Used on outbound PATCH/POST payloads
 * so that gated actions stay out of the request and any pre-existing
 * soft-deleted backend row is left untouched.
 */
export const filterVisiblePermissions = (permissions, isVisible) =>
  Object.fromEntries(
    Object.entries(permissions ?? {}).filter(([actionId]) => {
      const action = PERMISSION_ACTIONS_CONFIG[actionId];
      return action && isVisible(action);
    })
  );

const getHiddenSysconfigScopeRows = (profile, isVisible) =>
  Object.fromEntries(
    profile.profile_permissions
      .filter(
        ({ permission_action }) =>
          isSysconfigPermissionAction(permission_action) && !isVisible(PERMISSION_ACTIONS_CONFIG[permission_action])
      )
      .map(({ permission_action, permission_verbs }) => [permission_action, [...permission_verbs]])
  );

/**
 * The permissions a save of an existing profile sends: the form's values for the visible actions, plus
 * the stored rows of the granular sysconfig scopes hidden from this organization. The form leaves hidden
 * actions out and a save soft-deletes every row its payload omits, while a scope without a row follows
 * FULL on SYSTEM_CONFIGURATION: a None set on a scope that is hidden at the next save would otherwise
 * turn into the parent's FULL. A shown scope without a row still holding the value the dialog filled in
 * from the profile as loaded is left out, so it keeps following the SYSTEM_CONFIGURATION this save sets.
 */
export const getProfileSavePermissions = (permissions, profile, isVisible) => {
  const filledInValues = getMissingActionsValues(profile, isVisible);
  const shownPermissions = Object.fromEntries(
    Object.entries(filterVisiblePermissions(permissions, isVisible)).filter(
      ([actionId, verbs]) =>
        !(
          isSysconfigPermissionAction(actionId) &&
          actionId in filledInValues &&
          isEqual(verbs, filledInValues[actionId])
        )
    )
  );
  return { ...getHiddenSysconfigScopeRows(profile, isVisible), ...shownPermissions };
};

export const getMissingActionsValues = (profile, isVisible = () => true) => {
  const existingPermissions = profile.profile_permissions.map(({ permission_action }) => permission_action);
  const missingPermissions = {};
  const profileDefaultVerb = PERMISSION_VERBS_CONFIG.getVerbByOptionValue(profile.defaults);
  // A granular sysconfig scope without a row follows FULL on SYSTEM_CONFIGURATION, as isActionAllowed
  // resolves it: the profile's SYSTEM_CONFIGURATION row when it holds one, else its defaults.
  const systemConfigurationRow = profile.profile_permissions.find(
    ({ permission_action }) => permission_action === PERMISSION_ACTIONS_CONFIG.SYSTEM_CONFIGURATION.id
  );
  const holdsFullOnSystemConfiguration = (systemConfigurationRow?.permission_verbs ?? profile.defaults).includes(
    PERMISSION_VERBS_CONFIG.FULL.id
  );
  Object.values(PERMISSION_ACTIONS_CONFIG)
    .filter((action) => !existingPermissions.includes(action.id) && isVisible(action))
    .forEach((action) => {
      // Check for action-specific default_verb from PERMISSIONS_DESCRIPTIONS first
      const actionDesc = PERMISSIONS_DESCRIPTIONS[action.id];
      const actionDefaultVerb = actionDesc?.default_verb
        ? PERMISSION_VERBS_CONFIG.getVerbById(actionDesc.default_verb)
        : null;
      const effectiveDefaultVerb =
        holdsFullOnSystemConfiguration && isSysconfigPermissionAction(action.id)
          ? PERMISSION_VERBS_CONFIG.FULL
          : actionDefaultVerb || profileDefaultVerb;
      missingPermissions[action.id] =
        getMaxAllowedDefaultVerb(effectiveDefaultVerb, action.allowedVerbs)?.optionValue || [];
    });

  return missingPermissions;
};
